import { useEffect, useMemo, useState } from "react";
import type { ClarificationQuestion } from "@shared/domain";
import { apiClient } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { Sheet } from "@/components/ui/Sheet";
import { useClarifyTaskMutation } from "@/hooks/useBackendData";
import { useAppStore } from "@/state/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
  taskId?: string;
}

const chipClass = (active: boolean) =>
  active
    ? "border-[var(--accent-primary)] bg-[var(--accent-primary-subtle)] text-[var(--accent-primary)]"
    : "border-[var(--border-default)] bg-[var(--bg-tertiary)] text-[var(--text-secondary)]";

export const ClarifySheet = ({ open, onClose, taskId }: Props): JSX.Element | null => {
  const task = useAppStore((state) => state.tasks.find((item) => item.id === taskId));
  const clarifyTaskMutation = useClarifyTaskMutation();

  const [context, setContext] = useState("");
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questions, setQuestions] = useState<ClarificationQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;
    setLoadingQuestions(true);
    void apiClient
      .getClarificationQuestions(taskId)
      .then((result) => {
        if (cancelled) return;
        const fetched = Array.isArray(result.questions) ? result.questions : [];
        setQuestions(fetched);
        const initialAnswers = fetched.reduce<Record<string, string[]>>((acc, question) => {
          acc[question.id] = question.defaultOptionIds.length > 0 ? [...question.defaultOptionIds] : [];
          return acc;
        }, {});
        setAnswers(initialAnswers);
      })
      .catch(() => {
        if (cancelled) return;
        setQuestions([]);
        setAnswers({});
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingQuestions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, taskId]);

  const hasAtLeastOneAnswered = useMemo(
    () => Object.values(answers).some((selected) => Array.isArray(selected) && selected.length > 0),
    [answers]
  );

  if (!open || !taskId || !task) return null;

  return (
    <Sheet open={open} title="Let's clarify this task" onClose={onClose} height="70">
      <p className="mb-3 text-sm text-[var(--text-secondary)]">"{task.title}"</p>

      {loadingQuestions ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-[var(--border-default)] bg-[var(--bg-tertiary)] px-3 py-2">
            <span className="material-symbols-outlined animate-spin text-[18px] text-[var(--accent-primary)]">progress_activity</span>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">Drafting clarification questions...</p>
              <p className="text-xs text-[var(--text-secondary)]">This usually takes a few seconds.</p>
            </div>
          </div>
          <div className="card skeleton h-16 rounded-lg" />
          <div className="card skeleton h-16 rounded-lg" />
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((question, index) => {
            const selectedOptions = answers[question.id] ?? [];
            return (
              <div key={question.id}>
                <p className="mb-2 text-sm font-medium">
                  Q{index + 1}. {question.question}
                </p>
                <div className="flex flex-wrap gap-2">
                  {question.options.map((option) => {
                    const active = selectedOptions.includes(option);
                    return (
                      <button
                        key={option}
                        type="button"
                        className={`rounded-full border px-3 py-1.5 text-xs transition ${chipClass(active)}`}
                        onClick={() => {
                          setAnswers((prev) => {
                            const current = prev[question.id] ?? [];
                            if (question.type === "single") {
                              return { ...prev, [question.id]: [option] };
                            }
                            const next = current.includes(option) ? current.filter((item) => item !== option) : [...current, option];
                            return { ...prev, [question.id]: next };
                          });
                        }}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <InputField
            kind="textarea"
            label="Add context (optional)"
            textareaProps={{
              value: context,
              onChange: (event) => setContext(event.target.value),
              placeholder: "Any extra constraints or tooling requirements?"
            }}
          />

          <Button
            className="w-full"
            disabled={loadingQuestions || (!hasAtLeastOneAnswered && context.trim().length === 0) || clarifyTaskMutation.isPending}
            onClick={async () => {
              try {
                const payloadAnswers = Object.entries(answers)
                  .filter(([, selected]) => Array.isArray(selected) && selected.length > 0)
                  .map(([questionId, selectedOptionIds]) => ({ questionId, selectedOptionIds }));

                await clarifyTaskMutation.mutateAsync({
                  taskId,
                  payload: {
                    answers: payloadAnswers,
                    context: context.trim() || undefined
                  }
                });
                onClose();
              } catch {
                // toast emitted by mutation onError
              }
            }}
          >
            {clarifyTaskMutation.isPending ? "Submitting..." : "✦ Continue → Generate Plan"}
          </Button>
        </div>
      )}
    </Sheet>
  );
};
